import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { authStore } from '../../shared/store/auth.store.js'
import { ROUTES } from '../../app/routes.js'
import { ApiError } from '../../shared/types/api.js'

interface FormErrors {
  email?: string
  password?: string
  firstName?: string
  lastName?: string
  general?: string
}

export default function RegisterPage() {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)

  function validate(): boolean {
    const e: FormErrors = {}
    if (!email.trim()) e.email = t('error.emailRequired')
    else if (!/^[^\s@]+@[^\s@]+$/.test(email.trim())) e.email = t('error.emailInvalid')
    if (!password) e.password = t('error.passwordRequired')
    else if (password.length < 8) e.password = t('error.passwordMin')
    if (firstName.trim() && firstName.trim().length > 100) e.firstName = t('error.fieldRequired')
    if (lastName.trim() && lastName.trim().length > 100) e.lastName = t('error.fieldRequired')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const data: { email: string; password: string; firstName?: string; lastName?: string } = {
        email: email.trim(),
        password,
      }
      if (firstName.trim()) data.firstName = firstName.trim()
      if (lastName.trim()) data.lastName = lastName.trim()
      await authStore.register(data)
      // GuestGuard redirects to the app once auth status becomes 'authenticated'.
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 409) {
        setErrors({ email: err.message || 'Email already registered' })
      } else if (err instanceof ApiError && err.status === 400) {
        if (err.details && typeof err.details === 'object') {
          const fieldErrors: FormErrors = {}
          for (const [field, msg] of Object.entries(err.details)) {
            if (['email', 'password', 'firstName', 'lastName'].includes(field)) {
              (fieldErrors as any)[field] = String(msg)
            }
          }
          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors)
          } else {
            setErrors({ general: err.message || t('error.validation') })
          }
        } else {
          setErrors({ general: err.message || t('error.validation') })
        }
      } else if (err instanceof ApiError) {
        setErrors({ general: err.message || t('error.serverError') })
      } else {
        setErrors({ general: t('error.network') })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-logo">Magic Food</h1>
          <p className="auth-subtitle">{t('auth.register')}</p>
        </div>

        {errors.general && (
          <div className="form-error-banner">{errors.general}</div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-field">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setErrors({}) }}
              placeholder="user@example.com"
              autoComplete="email"
              required
              disabled={loading}
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-field">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setErrors({}) }}
              autoComplete="new-password"
              required
              disabled={loading}
            />
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="firstName">{t('auth.firstName')}</label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                autoComplete="given-name"
                disabled={loading}
              />
              {errors.firstName && <span className="field-error">{errors.firstName}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="lastName">{t('auth.lastName')}</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                autoComplete="family-name"
                disabled={loading}
              />
              {errors.lastName && <span className="field-error">{errors.lastName}</span>}
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? t('auth.registering') : t('auth.register')}
          </button>
        </form>

        <p className="auth-switch">
          {t('auth.hasAccount')}
          {' '}<a href={ROUTES.LOGIN}>{t('auth.loginLink')}</a>
        </p>
      </div>
    </div>
  )
}